import React, { useContext, useEffect } from 'react';

import { useInvertedLocalize } from '../../../utilities';

import { invertedMessages } from './messages';
import WindowContext from './WindowContext';

interface IdaasBridgeProps {
  /** Fired when the code is successfully verified. */
  onSuccess: () => void;
}

/**
 * This factor opens a popup window to initiate the IDaaS MFA flow, and listens
 * for messages from the popup conveying the result.
 *
 * A cryptographic nonce is generated per-mount and appended to the popup URL.
 * When FEATURE_MFA_NONCE_VALIDATION is enabled, the popup must echo the nonce
 * back in its BroadcastChannel message — preventing XSS-driven MFA bypass
 * (UT-XSS-06). Enable this flag once the backend popup page is updated to
 * include the nonce in its BroadcastChannel message.
 */
export default function IdaasBridge({
  onSuccess: handleSuccess,
}: IdaasBridgeProps): React.ReactNode {
  const isInitialRender = React.useRef(true);
  const l = useInvertedLocalize(invertedMessages);

  // Generate a cryptographic nonce once per component mount.
  const nonce = React.useRef<string>(
    Array.from(crypto.getRandomValues(new Uint8Array(16)))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('')
  );

  const { open } = useContext(WindowContext);

  useEffect(() => {
    const baseUrl = process.env.REACT_APP_NAV_API ?? '';

    if (isInitialRender.current) {
      isInitialRender.current = false;

      const width = Math.floor(window.innerWidth * 0.95);
      const height = Math.floor(window.innerHeight * 0.9);
      const left =
        window.screenLeft + Math.floor((window.innerWidth - width) / 2);
      const top =
        window.screenTop + Math.floor((window.innerHeight - height) / 2);

      // Pass nonce to popup so it can include it in the BroadcastChannel response.
      open(
        `${baseUrl}/ECMv6/api/authms/idaas/mfa/initiate?nonce=${nonce.current}`,
        'window:nav/mfa-prompt/idaas',
        `popup,width=${width},height=${height},top=${top},left=${left}`
      );
    }

    const handleMessage = (
      event: MessageEvent<{ isSuccess: boolean; nonce?: string }>
    ) => {
      // When FEATURE_MFA_NONCE_VALIDATION is enabled, validate nonce to prevent
      // XSS-driven MFA bypass (UT-XSS-06). The backend popup page must echo the
      // nonce (passed via URL param) back in its BroadcastChannel message.
      const nonceValid =
        !(window as Window & { envConfig?: Record<string, unknown> }).envConfig
          ?.FEATURE_MFA_NONCE_VALIDATION || event.data.nonce === nonce.current;

      if (event.data.isSuccess && nonceValid) {
        handleSuccess();
      }
    };

    const bc = new BroadcastChannel('channel:nav/mfa-prompt/idaas');

    bc.addEventListener('message', handleMessage);

    return () => {
      bc.removeEventListener('message', handleMessage);
      bc.close();
    };
  }, [handleSuccess, open]);

  return <>{l('A popup has been opened to verify your identity...')}</>;
}
